import React, { useMemo, useState } from 'react';

import { useAdHocRecipients } from '../../../lib/services/communication.service';
import { SearchableAutoComplete } from '../../_core/_ui/searchable-autocomplete.component';
import { CommunicationContextType, IAdHocAudience } from '../../../lib/types';
import { IPreviewRecipientOption } from './preview-panel.types';

interface IPreviewRecipientSelectProps {
  assessmentId?: string;
  // Same audience the preview/send resolves — a picked recipient is one of these.
  criteria: IAdHocAudience;
  contextType?: CommunicationContextType;
  contextId?: string;
  value: IPreviewRecipientOption | null;
  onChange: (option: IPreviewRecipientOption | null) => void;
  label: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  skip?: boolean;
}

// Shared "preview as" / "render variables from" picker: feeds the audience's
// server-paged players into a SearchableAutoComplete; used by preview + test-send.
export const PreviewRecipientSelect: React.FC<IPreviewRecipientSelectProps> = ({
  assessmentId,
  criteria,
  contextType,
  contextId,
  value,
  onChange,
  label,
  disabled = false,
  required = false,
  error = false,
  helperText,
  skip = false
}) => {
  const [search, setSearch] = useState('');

  const { items, loading } = useAdHocRecipients({
    assessmentId,
    criteria,
    contextType,
    contextId,
    search,
    skip: skip || disabled
  });

  const options = useMemo<IPreviewRecipientOption[]>(
    () => items.map((player) => ({ key: player.playerAssessmentId, label: player.playerName })),
    [items]
  );

  return (
    <SearchableAutoComplete
      options={options}
      value={value}
      onChange={onChange}
      onSearchChange={setSearch}
      loading={loading}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
      placeholder="Search recipients…"
      ariaLabel="Preview recipient"
    />
  );
};
